import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useActions } from "../context/ActionsContext";
import { PageHeader, TableWrap, Th, Td, Loading, EmptyState, Badge } from "../components/shared";
import { formatINR, formatDate } from "../lib/format";
import { CANDIDATE_STATUSES } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Search, UserPlus } from "lucide-react";

const STATUS_CLS = {
  New: "bg-slate-100 text-slate-700 border-slate-200", Active: "bg-blue-50 text-blue-700 border-blue-200",
  Processing: "bg-amber-50 text-amber-700 border-amber-200", Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Cancelled: "bg-rose-50 text-rose-700 border-rose-200", Refunded: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function Candidates() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { open } = useActions();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/candidates?search=${encodeURIComponent(search)}&status=${status}&limit=100`);
      setItems(data.items); setTotal(data.total);
    } finally { setLoading(false); }
  }, [search, status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("prabhat:refresh", h);
    return () => window.removeEventListener("prabhat:refresh", h);
  }, [load]);

  return (
    <div>
      <PageHeader title="Candidates" subtitle={`${total} candidates`}
        actions={<Button onClick={() => open("add_candidate")} data-testid="add-candidate-btn" className="bg-[#0F2942] hover:bg-[#163a5c] gap-1.5"><UserPlus className="h-4 w-4" /> Add Candidate</Button>} />
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
          <Input data-testid="candidate-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, mobile, ID, passport" className="pl-9" />
        </div>
        <select data-testid="candidate-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 text-sm">
          <option value="">All Status</option>
          {CANDIDATE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {loading ? <Loading /> : items.length === 0 ? <EmptyState title="No candidates yet" sub="Add your first candidate to start recording payments." /> : (
        <TableWrap>
          <thead><tr>
            <Th>Code</Th><Th>Name</Th><Th>Mobile</Th><Th>Country</Th><Th>Status</Th>
            <Th className="text-right">Received</Th><Th className="text-right">Advance Held</Th><Th className="text-right">Income</Th>
          </tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} data-testid={`candidate-row-${c.id}`} onClick={() => navigate(`/candidates/${c.id}`)} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer">
                <Td className="font-mono font-semibold text-[#0F2942] dark:text-teal-400">{c.candidate_code}</Td>
                <Td className="font-medium text-slate-900 dark:text-slate-100">{c.name}</Td>
                <Td>{c.mobile || "-"}</Td><Td>{c.country || "-"}</Td>
                <Td><Badge cls={STATUS_CLS[c.status] || STATUS_CLS.New}>{c.status}</Badge></Td>
                <Td className="text-right font-mono text-emerald-600">{formatINR(c.summary.total_received)}</Td>
                <Td className="text-right font-mono text-violet-600">{formatINR(c.summary.advance_held)}</Td>
                <Td className="text-right font-mono text-blue-600">{formatINR(c.summary.recognized_income)}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </div>
  );
}
